/**
 * @file useKeyboardHeight — how much of the window the on-screen keyboard covers.
 * @description SE-7388. Anything that decides whether a floating surface FITS
 * has to subtract this, because the window's own height will not do it.
 *
 * On Android 15+ the platform enforces edge-to-edge, so this app's configured
 * `softwareKeyboardLayoutMode: "resize"` no longer resizes the window — the
 * system reports an inset instead. `useWindowDimensions` therefore keeps
 * returning full height while the keyboard covers the bottom of it. Measured
 * on an S24 (Android 16): window 780dp, keyboard 294dp, so a panel deciding it
 * had ~1265px of room below actually had 125dp.
 *
 * Extracted rather than duplicated: `useDropdownPositioning` and the
 * `FilterChip` panel both need this number, and two copies of a platform
 * workaround drift apart exactly when one of them is fixed.
 * @module hooks/useKeyboardHeight
 */
import { useEffect, useState } from "react";
import { Keyboard, Platform } from "react-native";

/**
 * Tracks the on-screen keyboard's height in the units the layout uses (dp).
 *
 * `did*` rather than `will*`: Android only ever emits the `did` pair, and a
 * subscription that fired on iOS alone would leave the platform this defect
 * was measured on unfixed.
 * @returns The keyboard's height, or `0` while it is closed.
 */
export const useKeyboardHeight = (): number => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (Platform.OS === "web") return undefined;

    const shown = Keyboard.addListener("keyboardDidShow", event => {
      setHeight(event.endCoordinates.height);
    });
    const hidden = Keyboard.addListener("keyboardDidHide", () => {
      setHeight(0);
    });

    return () => {
      shown.remove();
      hidden.remove();
    };
  }, []);

  return height;
};
